import { Button } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";
import { useEffect } from "react";
import { FiSave } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import { useNavigate } from "react-router";
import { TiArrowBack } from "react-icons/ti";
import {
  ActionDto,
  AdministrativeContactDto,
  AttachmentDto,
  OperationalContactDto,
  ProjectDto,
  ProjectForCreationDto,
  ProjectForUpdateDto,
  ProjectFormDto,
  ProjectStatusDto,
  SignatoryAgreementDto,
  SignatoryOfAgreementDto,
  StructureBankAccountDto,
} from "../types";
import {
  ProjectsValidationSchema,
  generalInfosValidationSchema,
  administrativeContactValidationSchema,
  attachmentValidationSchema,
  operationalContactValidationSchema,
  signatoryAgreementValidationSchema,
  structureBankAccountValidationSchema,
} from "../validation";
import FormFieldInput from "../../../components/Forms/FormFieldInput";
import FormatedTextEditor from "../../../components/Forms/FormatedTextEditor";
import { useAddProject } from "../api/addProjects";
import { useUpdateProject } from "../api/updateProjects";
import { LoadingButton } from "@mui/lab";
import { Notifications } from "../../../components/notifications";
import FormFieldDateTime from "../../../components/Forms/FormFiledDateTime";
import FormFieldCheckbox from "../../../components/Forms/FormFieldCheckbox";
import FormFieldTextArea from "../../../components/Forms/FormFieldTextArea";
import { useDisclosure } from "@mantine/hooks";
import VerticalStepper from "../../../components/VerticalStepper/VerticalStepper";
import boule from "../../../resources/img/boule.png";
import FormField2Select from "../../../components/Forms/FormField2Select";
import { getProject, useGetAxe } from "../api";
import { AxeDto, AxeForCreationDto, AxeForUpdateDto } from "../../axes";
import { useAddSignatoryAgreement } from "../api/addSignatoryAgreement";
import { useAddAttachment } from "../api/addAttachment";
import { useAddAdministrativeContact } from "../api/addAdministrativeContact";
import { useAddOperationalContact } from "../api/addOperationalContact";
import { useAddStructureBankAccount } from "../api/addStructureBankAccount";
import { SectionDto } from "../../sections";
import { InputNumber } from "primereact/inputnumber";
import { AnyObject, ObjectSchema } from "yup";
import FormFieldCurrency from "../../../components/Forms/FormFieldCurrency";
import { selectCurrentUser } from "../../../auth/AuthUserSlice";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FormFieldCountrySelect from "../../../components/Forms/FormFieldCountrySelect/FormFieldCountrySelect";
import { FileUpload, FileUploadHandlerEvent, FileUploadSelectEvent } from "primereact/fileupload";
import FormFieldFileChooser from "../../../components/Forms/FormFieldFileChooser";
import { getSignatoryAgreement } from "../api/getSignatoryAgreement";
import { getAdministrativeContact } from "../api/getAdministrativeContact";
import { getOperationalContact } from "../api/getOperationalContact";
import { getStructureBankAccount } from "../api/getStructureBankAccount";
import { getAttachment } from "../api/getAttachment";
import axios from "axios";
import { ProjectRecap } from "./ProjectRecap";
import { useUpdateSignatoryAgreement } from "../api/updateSignatoryAgreement";
import { useUpdateAdministrativeContact } from "../api/updateAdministrativeContact";
import { useUpdateOperationalContact } from "../api/updateOperationalContact";
import { useUpdateStructureBankAccount } from "../api/updateStructureBankAccount";
import { useUpdateAttachment } from "../api/updateAttachment";
import { useSetProjectStatus } from "../api/setProjectStatus";

import * as yup from "yup";

interface Props {
  defaultProject?: ProjectDto;
  defaultAxe?: AxeDto;
}

export interface StepProp {
  label: string;
  code: string;
}

interface SubmitProjectsProps {
  data: ProjectForCreationDto | ProjectForUpdateDto;
  formikHelpers: FormikHelpers<ProjectForCreationDto | ProjectForUpdateDto>;
}

interface FormikProps {
  isValid?: boolean;
  dirty?: boolean;
  errors?: FormikErrors<ProjectFormDto>;
  isSubmitting?: boolean;
  values?: ProjectFormDto;
  setFieldValue?: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<ProjectFormDto>>;
  handleReset?: () => void;
  handleChange?: {
      /** Classic React change handler, keyed by input name */
      (e: React.ChangeEvent<any>): void;
      /** Preact-like linkState. Will return a handleChange function.  */
      <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any> ? void : (e: string | React.ChangeEvent<any>) => void;
  };
}

const ProjectsForm = ({ defaultProject, defaultAxe }: Props) => {
  const currentUser = useSelector(selectCurrentUser);
  const { t, i18n } = useTranslation("common");
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Current lang
  let lang = i18n.language;

  const fileApplicationFileRef = useRef<FileUpload>(null);
  const fileBudgetRef = useRef<FileUpload>(null);
  const fileChronogramRef = useRef<FileUpload>(null);
  const fileOtherFileRef = useRef<FileUpload>(null);
  const filePreviousAnnualReportRef = useRef<FileUpload>(null);
  const fileStatutsRef = useRef<FileUpload>(null);

  const navigate = useNavigate();
  const [project, setProject] = useState({
    name: defaultProject?.name??"",
    summary: defaultProject?.summary??"",
    beneficiaryNumber: defaultProject?.beneficiaryNumber??undefined,
    locationCountry: defaultProject?.locationCountry??"",
    locationCity: defaultProject?.locationCity??"",
    financialSupportRequest: defaultProject?.financialSupportRequest??0,
    startDate: defaultProject?.startDate??new Date(),
    endDate: defaultProject?.endDate??new Date(),
    step: defaultProject?.step??"",
    provisionalBudget: defaultProject?.provisionalBudget??0,
    useOfSupportRequest: defaultProject?.useOfSupportRequest??"",
    otherFinancialPartner: defaultProject?.otherFinancialPartner??"",
    comment: defaultProject?.comment??"",
    emailAccount: defaultProject?.emailAccount??currentUser?.preferred_username,
    consultantInvolved: defaultProject?.consultantInvolved??"",
    status: defaultProject?.status??"",

    // SubBloc of project
    idAxe: defaultProject?.idAxe??defaultAxe?.id,
    idSignatoryAgreement: defaultProject?.idSignatoryAgreement??"",
    idAdministrativeContact: defaultProject?.idAdministrativeContact??"",
    idOperationalContact: defaultProject?.idOperationalContact??"",
    idStructureBankAccount: defaultProject?.idStructureBankAccount??"",
    idAccount: defaultProject?.idAccount??"",
    idAttachment: defaultProject?.idAttachment??"",
  } as ProjectForCreationDto | ProjectForUpdateDto);

  const [signatoryForm, setSignatoryForm] = useState({
    // SignatoryAgreement
    lastname: "",
    firstname: "",
    mobilePhone: "",
    email: "",
    qualityOfSignatory: "",
    fixPhone: "",
  } as SignatoryAgreementDto);

  const [administrativeForm, setAdministrativeForm] = useState({
    // AdministrativeContact
    firstname: "",
    lastname: "",
    mobilePhone: "",
    email: "",
    civility: "",
    fixPhone: "",
    region: "",
    city: "",
    postalBox: "",
    function: "",
  } as AdministrativeContactDto);

  const [operationalForm, setOperationalForm] = useState({
    // OperationalContact
    firstname: "",
    lastname: "",
    function: "",
    regionOfContact: "",
    cityOfContact: "",
    postalBoxOfContact: "",
    streetAndNumber: "",
    mobilePhone: "",
    email: "",
    departmentOfContact: "",
    adresse: "",
    fixPhone: "",
    districtOfContact: "",
  } as OperationalContactDto);

  const [bankForm, setBankForm] = useState({
    // StructureBankAccount
    accountOwner: "",
    bankName: "",
    bankAddress: "",
    cityOfBank: "",
    accountNumber: "",
    poBox: "",
    iban: "",
    swiftCode: "",
  } as StructureBankAccountDto);

  const [attachmentForm, setAttachmentForm] = useState({
    // Attachment
    applicationFile: null,
    statuts: null,
    otherFile: null,
    previousAnnualReport: null,
    budget: null,
    chronogram: null,
    otherFile1: null,
    otherFile2: null,
    otherFile3: null,
    otherFile4: null,
  } as AttachmentDto);

  const [axe, setAxe] = useState({
    label: "",
    description: "",
    sections: [],
  } as AxeForCreationDto | AxeForUpdateDto);

  const [applicationFile, setApplicationFile] = useState<File>();
  const [statuts, setStatuts] = useState<File>();
  const [otherFile, setOtherFile] = useState<File>();
  const [previousAnnualReport, setPreviousAnnualReport] = useState<File>();
  const [budget, setBudget] = useState<File>();
  const [chronogram, setChronogram] = useState<File>();

  // handle default change request for update
  useEffect(() => {
    if (defaultProject && defaultProject?.id) {
       setProject(defaultProject); 
    }
    if (defaultAxe && defaultAxe?.id) setAxe(defaultAxe);
  }, [defaultProject, defaultAxe]);


  const createProjectApi = useAddProject(lang ?? "fr");
  const updateProjectApi = useUpdateProject();
  function updateLastProject(
    id: string,
    data: ProjectForCreationDto | ProjectForUpdateDto,
    handleReset?: any
  ) {
    if (id === null || id === undefined) return;

    updateProjectApi
      .mutateAsync({ id, data })
      .then((response) => {
            setLoading(false);
            // Notifications.success("Projet créé avec succès");
            if(!defaultProject) setProject(response);
            setActiveStep(activeStep + 1);
            reloadPreviousObject(steps[activeStep + 1].code);
            handleReset();
      })
      .catch((e) => {
            setLoading(false);
            Notifications.error("Erreur lors de la mise à jour du projet.");
            console.error(e);
      });
  }

  function createEmptyProject(data: ProjectForCreationDto, handleReset?: any) {
    createProjectApi
      .mutateAsync(data)
      .then((response) => {
        // Notifications.success("Projet créé avec succès");
        setLoading(false);
        if(!defaultProject) setProject(response);
        setActiveStep(activeStep + 1);
        handleReset();
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error("Erreur lors de la creation du projet.");
        console.error(e);
      });
  }

  const createSignatoryAgreementApi = useAddSignatoryAgreement(project.id!);
  function createSignatoryAgreement(data: SignatoryAgreementDto, handleReset?: any) {
    createSignatoryAgreementApi
      .mutateAsync(data)
      .then((response) => {
        setLoading(false);
        Notifications.success("Signataire de la convention/autre créé avec succès");
        setActiveStep(activeStep + 1);
        handleReset();
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error(
          "Erreur lors de la creation du Signataire de la convention/autre."
        );
        console.error(e);
      });
  }

  const createAdministrativeContactApi = useAddAdministrativeContact(
    project.id!
  );
  function createAdministrativeContact(data: AdministrativeContactDto, handleReset?: any) {
    createAdministrativeContactApi
      .mutateAsync(data)
      .then((response) => {
        setLoading(false);
        Notifications.success("Contact Administratif créé avec succès");
        setActiveStep(activeStep + 1);
        handleReset();
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error(
          "Erreur lors de la creation du Contact Administratif."
        );
        console.error(e);
      });
  }

  const createOperationalContactApi = useAddOperationalContact(project.id!);
  function createOperationalContact(data: OperationalContactDto, handleReset?: any) {
    createOperationalContactApi
      .mutateAsync(data)
      .then((response) => {
        setLoading(false);
        Notifications.success("Contact Opérationel créé avec succès");
        setActiveStep(activeStep + 1);
        handleReset();
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error(
          "Erreur lors de la creation du Contact Opérationel."
        );
        console.error(e);
      });
  }

  const createStructureBankAccountApi = useAddStructureBankAccount(project.id!);
  function createStructureBankAccount(data: StructureBankAccountDto, handleReset?: any) {
    createStructureBankAccountApi
      .mutateAsync(data)
      .then((response) => {
        setLoading(false);
        Notifications.success("Compte de la structure créé avec succès");
        setActiveStep(activeStep + 1);
        handleReset();
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error(
          "Erreur lors de la creation du Compte de la structure."
        );
        console.error(e);
      });
  }

  const createAttachmentApi = useAddAttachment(project.id!);
  function createAttachment(data: AttachmentDto, handleReset?: any) {
    let formData = new FormData();

    formData.append("applicationFile", applicationFile as any);
    formData.append("statuts", statuts as any);
    formData.append("otherFile", otherFile as any);
    formData.append("budget", budget as any);
    formData.append("chronogram", chronogram as any);
    formData.append("previousAnnualReport", previousAnnualReport as any);
    // console.log("Attachment ", formData);
    setLoading(true);
    createAttachmentApi
      .mutateAsync(formData)
      .then((response) => {
        setLoading(false);
        Notifications.success("Pièces jointes créé avec succès");
        setActiveStep(activeStep + 1);
        handleReset();
        // navigate("/list/projects");
      })
      .catch((e) => {
        setLoading(false);
        Notifications.error("Erreur lors de la creation des pièces jointes.");
        console.error(e);
      });
  }


  const updateSignatoryAgreementApi = useUpdateSignatoryAgreement();
  function updateLastSignatoryAgreement(id: string, data: SignatoryAgreementDto, handleReset?: any) {
      if (id === null || id === undefined) return;

      updateSignatoryAgreementApi
        .mutateAsync({ id, data })
        .then((response) => {
              setLoading(false);
              // Notifications.success("Projet créé avec succès");
              setActiveStep(activeStep + 1);
              reloadPreviousObject(steps[activeStep + 1].code);
              handleReset();
        })
        .catch((e) => {
              setLoading(false);
              Notifications.error("Erreur lors de la mise à jour du Signataire de la convention/autre.");
              console.error(e);
        });
  }

  const updateAdministrativeContactApi = useUpdateAdministrativeContact();
  function updateLastAdministrativeContact(id: string, data: AdministrativeContactDto, handleReset?: any) {
      if (id === null || id === undefined) return;

      updateAdministrativeContactApi
        .mutateAsync({ id, data })
        .then((response) => {
              setLoading(false);
              // Notifications.success("Projet créé avec succès");
              setActiveStep(activeStep + 1);
              reloadPreviousObject(steps[activeStep + 1].code);
              handleReset();
        })
        .catch((e) => {
              setLoading(false);
              Notifications.error("Erreur lors de la mise à jour du contact administratif.");
              console.error(e);
        });
  }


  const updateOperationalContactApi = useUpdateOperationalContact();
  function updateLastOperationalContact(id: string, data: OperationalContactDto, handleReset?: any) {
      if (id === null || id === undefined) return;

      updateOperationalContactApi
        .mutateAsync({ id, data })
        .then((response) => {
              setLoading(false);
              // Notifications.success("Projet créé avec succès");
              setActiveStep(activeStep + 1);
              reloadPreviousObject(steps[activeStep + 1].code);
              handleReset();
        })
        .catch((e) => {
              setLoading(false);
              Notifications.error("Erreur lors de la mise à jour du contact opérationnel.");
              console.error(e);
        });
  }

  const updateStructureBankAccountApi = useUpdateStructureBankAccount();
  function updateLastStructureBankAccount(id: string, data: StructureBankAccountDto, handleReset?: any) {
      if (id === null || id === undefined) return;

      updateStructureBankAccountApi
        .mutateAsync({ id, data })
        .then((response) => {
              setLoading(false);
              // Notifications.success("Projet créé avec succès");
              setActiveStep(activeStep + 1);
              reloadPreviousObject(steps[activeStep + 1].code);
              handleReset();
        })
        .catch((e) => {
              setLoading(false);
              Notifications.error("Erreur lors de la mise à jour de la banque.");
              console.error(e);
        });
  }

  const updateAttachmentApi = useUpdateAttachment();
  function updateLastAttachment(id: string, data: AttachmentDto, handleReset?: any) {
      if (id === null || id === undefined) return;

      let formData = new FormData();

      formData.append("applicationFile", applicationFile as any);
      formData.append("statuts", statuts as any);
      formData.append("otherFile", otherFile as any);
      formData.append("budget", budget as any);
      formData.append("chronogram", chronogram as any);
      formData.append("previousAnnualReport", previousAnnualReport as any);
      // console.log("Attachment ", formData);

      setLoading(true);
      updateAttachmentApi
        .mutateAsync({id, formData})
        .then((response) => {
              setLoading(false);
              Notifications.success("Pièces jointes mises à jour avec succès");
              setActiveStep(activeStep + 1);
              handleReset();
        })
        .catch((e) => {
              setLoading(false);
              Notifications.error("Erreur lors de la mise à jour de les pieces jointes.");
              console.error(e);
        });
  }

  const updateProjectStatusApi = useSetProjectStatus(project.id!, lang??'fr');

  function submitStep(step: string, values: any, handleReset?: any) {
    console.log("Project ", values);
    switch (step) {
      case "PR_GI": {
        let newValues = values as ProjectDto;
        let newProject = {
          name: newValues.name,
          summary: newValues.summary,
          beneficiaryNumber: newValues.beneficiaryNumber,
          startDate: newValues.startDate,
          endDate: newValues.endDate,
          step: newValues.step,
          provisionalBudget: newValues.provisionalBudget,
          useOfSupportRequest: newValues.useOfSupportRequest,
          otherFinancialPartner: newValues.otherFinancialPartner,
          comment: newValues.comment,
          // idAttachment: "",
          locationCountry: newValues.locationCountry,
          locationCity: newValues.locationCity,
          financialSupportRequest: newValues.financialSupportRequest,
          idAxe: axe?.id,
          status: "Draft",
          emailAccount: currentUser?.preferred_username,
          consultantInvolved: newValues.consultantInvolved,
          // idSignatoryAgreement: "",
          // idAdministrativeContact: "",
          // idOperationalContact: "",
          // idStructureBankAccount: "",
          // idAccount: "",
        } as ProjectForCreationDto;

        // console.log("GeneralInfos ", newProject);
        if(project && project.id) {
          // update GeneralInfos
          updateLastProject(project.id, newProject, handleReset);
        } else {
          createEmptyProject(newProject, handleReset);
        }
        break;
      }
      case "PR_SA": {
        let newValues = values as SignatoryAgreementDto;
        let signatoryAgreement = {
          lastname: newValues.lastname,
          firstname: newValues.firstname,
          mobilePhone: newValues.mobilePhone,
          email: newValues.email,
          qualityOfSignatory: newValues.qualityOfSignatory,
          fixPhone: newValues.fixPhone,
        } as SignatoryAgreementDto;

        // console.log("SignatoryAgreement ", signatoryAgreement);
        if(project && project.idSignatoryAgreement) {
          // update SignatoryAgreement
          updateLastSignatoryAgreement(project.idSignatoryAgreement, signatoryAgreement, handleReset);
        } else {
          createSignatoryAgreement(signatoryAgreement, handleReset);
        }
        break;
      }
      case "PR_AC": {
        let newValues = values as AdministrativeContactDto;
        let administrativeContact = {
          firstname: newValues.firstname,
          lastname: newValues.lastname,
          mobilePhone: newValues.mobilePhone,
          email: newValues.email,
          civility: newValues.civility,
          fixPhone: newValues.fixPhone,
          region: newValues.region,
          city: newValues.city,
          postalBox: newValues.postalBox,
          function: newValues.function
        } as AdministrativeContactDto;

        // console.log("AdministrativeContact ", administrativeContact);
        if(project && project.idAdministrativeContact) {
          // update AdministrativeContact
          updateLastAdministrativeContact(project.idAdministrativeContact, administrativeContact, handleReset);
        } else {
          createAdministrativeContact(administrativeContact, handleReset);
        }
        break;
      }
      case "PR_OC": {
        let newValues = values as OperationalContactDto;
        let operationalContact = {
          firstname: newValues.firstname,
          lastname: newValues.lastname,
          function: newValues.function,
          regionOfContact: newValues.regionOfContact,
          cityOfContact: newValues.cityOfContact,
          postalBoxOfContact: newValues.postalBoxOfContact,
          streetAndNumber: newValues.streetAndNumber,
          mobilePhone: newValues.mobilePhone,
          email: newValues.email,
          departmentOfContact: newValues.departmentOfContact,
          fixPhone: newValues.fixPhone,
          districtOfContact: newValues.districtOfContact,
          adresse: newValues.adresse,
        } as OperationalContactDto;

        // console.log("OperationalContact ", operationalContact);
        if(project && project.idOperationalContact) {
          // update OperationalContact
          updateLastOperationalContact(project.idOperationalContact, operationalContact, handleReset);
        } else {
          createOperationalContact(operationalContact, handleReset);
        }
        break;
      }
      case "PR_SBA": {
        let newValues = values as StructureBankAccountDto;
        let structureBankAccount = {
          accountOwner: newValues.accountOwner,
          bankName: newValues.bankName,
          bankAddress: newValues.bankAddress,
          cityOfBank: newValues.cityOfBank,
          accountNumber: newValues.accountNumber,
          poBox: newValues.poBox,
          iban: newValues.iban,
          swiftCode: newValues.swiftCode,
        } as StructureBankAccountDto;

        // console.log("StructureBankAccount ", structureBankAccount);
        if(project && project.idStructureBankAccount) {
          // update StructureBankAccount
          updateLastStructureBankAccount(project.idStructureBankAccount, structureBankAccount, handleReset);
        } else {
          createStructureBankAccount(structureBankAccount, handleReset);
        }
        break;
      }
      case "PR_FA": {
        let newValues = values as AttachmentDto;
        let attachment = {
          applicationFile: newValues.applicationFile,
          statuts: newValues.statuts,
          otherFile: newValues.otherFile,
          previousAnnualReport: newValues.previousAnnualReport,
          budget: newValues.budget,
          chronogram: newValues.chronogram,
          otherFile1: newValues.otherFile1,
          otherFile2: newValues.otherFile2,
          otherFile3: newValues.otherFile3,
          otherFile4: newValues.otherFile4,
        } as AttachmentDto;

        // console.log("Attachment ", attachment);
        if(project && project.idAttachment) {
          // update Attachment
          updateLastAttachment(project.idAttachment, attachment, handleReset);
        } else {
          createAttachment(attachment, handleReset);
        }
        break;
      }
      case "Confirmation": {
        // TODO: update project status api
        console.log("Confirmation");
        setLoading(true);
        updateProjectStatusApi
          .mutateAsync({ status: "Created" } as ProjectStatusDto)
          .then((response) => {
              setLoading(false);
              Notifications.success("Création de projet finalisé !!");
              navigate("/list/projects");
          })
          .catch((e) => {
              setLoading(false);
              console.error(e);
              Notifications.error("Une erreur est survenue lors de la finalisation du processus."); 
          });
        break;
      }
      default: {
        console.log("Do nothing");
        break;
      }
    }
  }


  const GeneralInfos = () => {
    // General Informations
    /*
        name: string;
        summary: string;
        beneficiaryNumber: number;
        locationCountry: string;
        locationCity: string;
        startDate: Date | string;
        endDate: Date | string;
        provisionalBudget: number;
        useOfSupportRequest: string;
        otherFinancialPartner: string;
        comment: string;
    */
    return (
          <>
              <Formik
                    initialValues={project}
                    validationSchema={generalInfosValidationSchema}
                    onSubmit={(values, formikHelpers) => {
                      if(steps[activeStep + 1].code == "PR_FA") {
                        console.log("LOADATTACHEDFILES");
                        loadAttachedFiles(formikHelpers.setFieldValue);
                      }
                      setLoading(true);
                      submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
                     }
                    }
                    isInitialValid={project ? true : false}
                    // validateOnChange={false}
                    validateOnMount={false}
                    // initialStatus={true}
                    enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
                  >
                    {({
                      isValid,
                      dirty,
                      errors,
                      isSubmitting,
                      values,
                      setFieldValue,
                      handleReset,
                      handleChange,
                      resetForm,
                      validateForm
                    }) => {
                      console.log("isValid ", isValid);
                      console.log("dirty ", dirty);
                      console.log("values ", values);
                      console.log("ERRORS ", errors);
                      return (
                        <Form>
                          <div className="d-flex flex-column">
                            {/* <div className="row mb-4" hidden>
                                <div className="col-12 col-md-6  mt-4" >
                                  <FormFieldInput name="idAccount" setFieldValue={formikProps.setFieldValue} label={"idAccount"}  value={1} />
                                </div>
                                <div className="col-12 col-md-6  mt-4">
                                  <FormFieldInput name="idAxe" label={"idAxe"} setFieldValue={formikProps.setFieldValue}  value={defaultAxe?.id} />
                                </div>
                              </div> */}
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  name="name"
                                  label={t("projet.generalInfos.name")}
                                  setFieldValue={setFieldValue}
                                  value={values?.name}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  name="beneficiaryNumber"
                                  type="number"
                                  label={t("projet.generalInfos.beneficiaryNumber")}
                                  value={values?.beneficiaryNumber}
                                  setFieldValue={setFieldValue}
                                  required
                                />
                              </div>
                            </div>

                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput name="locationCountry" label={ t('projet.generalInfos.locationCountry') } value={ formikProps.values?.locationCountry ? formikProps.values.locationCountry : projectForm.locationCountry }  required /> */}
                                <FormFieldCountrySelect
                                  name="locationCountry"
                                  lang={lang}
                                  label={t("projet.generalInfos.locationCountry")}
                                  setFieldValue={setFieldValue}
                                  value={values?.locationCountry}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  name="locationCity"
                                  label={t("projet.generalInfos.locationCity")}
                                  setFieldValue={setFieldValue}
                                  value={values?.locationCity}
                                  required
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  type="date"
                                  name="startDate"
                                  label={t("projet.generalInfos.startDate")}
                                  setFieldValue={setFieldValue}
                                  value={values?.startDate}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  type="date"
                                  name="endDate"
                                  label={t("projet.generalInfos.endDate")}
                                  setFieldValue={setFieldValue}
                                  value={values?.endDate}
                                  required
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput type="number" name="provisionalBudget" label={"Budget prévisionnel"} value={ formikProps.values?.provisionalBudget ? formikProps.values.provisionalBudget : projectForm.provisionalBudget } setFieldValue={formikProps.setFieldValue} required /> */}
                                <FormFieldCurrency
                                  name="provisionalBudget"
                                  label={t("projet.generalInfos.provisionalBudget")}
                                  value={values?.provisionalBudget}
                                  setFieldValue={setFieldValue}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput type="number" name="financialSupportRequest" label={"Demande de financement"} value={ formikProps.values?.financialSupportRequest ? formikProps.values.financialSupportRequest : projectForm.financialSupportRequest } setFieldValue={formikProps.setFieldValue} required /> */}
                                <FormFieldCurrency
                                  name="financialSupportRequest"
                                  label={t("projet.generalInfos.financialSupportRequest")}
                                  value={values?.financialSupportRequest}
                                  setFieldValue={setFieldValue}
                                  required
                                />
                              </div>
                            </div>

                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <FormFieldTextArea
                                  name="useOfSupportRequest"
                                  setFieldValue={setFieldValue}
                                  label={t("projet.generalInfos.useOfSupportRequest")}
                                  value={values?.useOfSupportRequest}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <FormFieldTextArea
                                  name="otherFinancialPartner"
                                  setFieldValue={setFieldValue}
                                  label={t("projet.generalInfos.otherFinancialPartner")}
                                  value={values?.otherFinancialPartner}
                                  required
                                />
                              </div>
                            </div>

                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <FormFieldTextArea
                                  name="summary"
                                  label={t("projet.generalInfos.summary")}
                                  value={values?.summary}
                                  required
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                <FormFieldTextArea
                                  name="comment"
                                  label={t("projet.generalInfos.comment")}
                                  value={values?.comment}
                                  required
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <FormFieldInput
                                  type="text"
                                  name="consultantInvolved"
                                  setFieldValue={setFieldValue}
                                  label={t("projet.generalInfos.consultantInvolved")}
                                  value={values?.consultantInvolved}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-end">
                              {activeStep !== steps.length - 1 && (
                                  <LoadingButton
                                    type="submit"
                                    disabled={!isValid} //!(isValid && dirty)
                                    loading={loading}
                                    children={t("app.next")}
                                    style={{
                                      marginLeft: "10px",
                                      color: "#fff",
                                      backgroundColor: "#FF7900",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                )}
                          </div>
                        </Form>
                      );
                  }}
              </Formik>
          </>
    )
  };

  const StructureBankAccount = () => {
    // StructureBankAccount
    /*
      accountOwner: string;
      bankName: string;
      bankAddress: string;
      cityOfBank: string;
      accountNumber: string;
      poBox: string;
      iban: string;
      swiftCode: string;
    */
    return (
      <>
          <Formik
                initialValues={bankForm}
                validationSchema={structureBankAccountValidationSchema}
                onSubmit={(values, formikHelpers) => {
                  if(steps[activeStep + 1].code == "PR_FA") {
                    console.log("LOADATTACHEDFILES");
                    loadAttachedFiles(formikHelpers.setFieldValue);
                  }
                  setLoading(true);
                  submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
                 }
                }
                isInitialValid={bankForm ? true : false}
                // validateOnChange={false}
                validateOnMount={false}
                // initialStatus={true}
                enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
              >
                {({
                  isValid,
                  dirty,
                  errors,
                  isSubmitting,
                  values,
                  setFieldValue,
                  handleReset,
                  handleChange,
                  resetForm
                }) => {
                  console.log("isValid ", isValid);
                  console.log("dirty ", dirty);
                  console.log("ERRORS ", errors);
                  return (
                    <Form>
                        <div className="d-flex flex-column">
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="accountOwner"
                                setFieldValue={setFieldValue}
                                value={
                                  values?.accountOwner
                                    ? values?.accountOwner
                                    : bankForm?.accountOwner
                                }
                                label={t("projet.bank.accountOwner")}
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="accountNumber"
                                setFieldValue={setFieldValue}
                                value={
                                  values?.accountNumber
                                    ? values?.accountNumber
                                    : bankForm?.accountNumber
                                }
                                label={t("projet.bank.accountNumber")}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="bankName"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.bankName")}
                                value={
                                  values?.bankName
                                    ? values?.bankName
                                    : bankForm?.bankName
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="bankAddress"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.bankAddress")}
                                value={
                                  values?.bankAddress
                                    ? values?.bankAddress
                                    : bankForm?.bankAddress
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="cityOfBank"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.cityOfBank")}
                                value={
                                  values?.cityOfBank
                                    ? values?.cityOfBank
                                    : bankForm?.cityOfBank
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="poBox"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.poBox")}
                                value={
                                  values?.poBox
                                    ? values?.poBox
                                    : bankForm?.poBox
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="iban"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.iban")}
                                value={
                                  values?.iban
                                    ? values?.iban
                                    : bankForm?.iban
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="swiftCode"
                                setFieldValue={setFieldValue}
                                label={t("projet.bank.swiftCode")}
                                value={
                                  values?.swiftCode
                                    ? values?.swiftCode
                                    : bankForm?.swiftCode
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          {activeStep !== 0 && activeStep !== steps.length - 1 && (
                                <Button
                                  type="button"
                                  onClick={() => {
                                    reloadPreviousObject(
                                      steps[activeStep - 1].code,
                                      setFieldValue
                                    );
                                    setActiveStep(activeStep - 1);
                                  }}
                                  children={t("app.previous")}
                                  style={{
                                    marginRight: "10px",
                                    border: "1px solid #979797",
                                    color: "#757575",
                                    backgroundColor: "#fff",
                                    width: "100px !important",
                                  }}
                                  sx={{
                                    "&.MuiButton-root": {
                                      textTransform: "none",
                                      height: "32px",
                                      width: "100px",
                                    },
                                    "&.Mui-disabled": {
                                      color: "white",
                                    },
                                  }}
                                />
                            )}
                              {activeStep !== steps.length - 1 && (
                                  <LoadingButton
                                    type="submit"
                                    disabled={!isValid} //!(isValid && dirty)
                                    loading={loading}
                                    children={t("app.next")}
                                    style={{
                                      marginLeft: "10px",
                                      color: "#fff",
                                      backgroundColor: "#FF7900",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                )}
                        </div>
                    </Form>
                  );
              }}
          </Formik>
      </>
    )
  };

  const OperationalContact = () => {
    // OperationalContact
    /*
      firstname: string;
      lastname: string;
      function: string;
      regionOfContact: string;
      cityOfContact: string;
      postalBoxOfContact: string;
      streetAndNumber: string;
      mobilePhone: string;
      email: string;
      departmentOfContact: string;

    */
    return (
      <>
          <Formik
                initialValues={operationalForm}
                validationSchema={operationalContactValidationSchema}
                onSubmit={(values, formikHelpers) => {
                  if(steps[activeStep + 1].code == "PR_FA") {
                    console.log("LOADATTACHEDFILES");
                    loadAttachedFiles(formikHelpers.setFieldValue);
                  }
                  setLoading(true);
                  submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
                 }
                }
                isInitialValid={operationalForm ? true : false}
                // validateOnChange={false}
                validateOnMount={false}
                // initialStatus={true}
                enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
              >
                {({
                  isValid,
                  dirty,
                  errors,
                  isSubmitting,
                  values,
                  setFieldValue,
                  handleReset,
                  handleChange,
                  resetForm
                }) => {
                  console.log("isValid ", isValid);
                  console.log("dirty ", dirty);
                  console.log("ERRORS ", errors);
                  return (
                    <Form>
                        <div className="d-flex flex-column">
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="lastname"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.lastname")}
                                value={
                                  values?.lastname
                                    ? values?.lastname
                                    : operationalForm?.lastname
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="firstname"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.firstname")}
                                value={
                                  values?.firstname
                                    ? values?.firstname
                                    : operationalForm?.firstname
                                }
                                required
                              />
                            </div>
                          </div>

                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="mobilePhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.mobilePhone")}
                                value={
                                  values?.mobilePhone
                                    ? values?.mobilePhone
                                    : operationalForm?.mobilePhone
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="fixPhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.fixPhone")}
                                value={
                                  values?.fixPhone
                                    ? values?.fixPhone
                                    : operationalForm?.fixPhone
                                }
                                
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="adresse"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.adresse")}
                                value={
                                  values?.adresse
                                    ? values?.adresse
                                    : operationalForm?.adresse
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="email"
                                setFieldValue={setFieldValue}
                                type="email"
                                label={t("projet.operational.email")}
                                value={
                                  values?.email
                                    ? values?.email
                                    : operationalForm?.email
                                }
                                required
                              />
                            </div>
                          </div>

                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="regionOfContact"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.regionOfContact")}
                                value={
                                  values?.regionOfContact
                                    ? values?.regionOfContact
                                    : operationalForm?.regionOfContact
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="departmentOfContact"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.departmentOfContact")}
                                value={
                                  values?.departmentOfContact
                                    ? values?.departmentOfContact
                                    : operationalForm?.departmentOfContact
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="cityOfContact"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.cityOfContact")}
                                value={
                                  values?.cityOfContact
                                    ? values?.cityOfContact
                                    : operationalForm?.cityOfContact
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="streetAndNumber"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.streetAndNumber")}
                                value={
                                  values?.streetAndNumber
                                    ? values?.streetAndNumber
                                    : operationalForm?.streetAndNumber
                                }
                                required
                              />
                            </div>
                          </div>

                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="postalBoxOfContact"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.postalBoxOfContact")}
                                value={
                                  values?.postalBoxOfContact
                                    ? values?.postalBoxOfContact
                                    : operationalForm?.postalBoxOfContact
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="function"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.function")}
                                value={
                                  values?.function
                                    ? values?.function
                                    : operationalForm?.function
                                }
                                required
                              />
                            </div>
                          </div>

                          {/* <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="districtOfContact"
                                setFieldValue={formikProps.setFieldValue}
                                label={t("projet.operational.districtOfContact")}
                                value={
                                  formikProps.values?.districtOfContact
                                    ? formikProps.values.districtOfContact
                                    : projectForm.districtOfContact
                                }
                                
                              />
                            </div>
                          </div> */}
                        </div>

                        <div className="d-flex justify-content-end">
                          {activeStep !== 0 && activeStep !== steps.length - 1 && (
                                <Button
                                  type="button"
                                  onClick={() => {
                                    reloadPreviousObject(
                                      steps[activeStep - 1].code,
                                      setFieldValue
                                    );
                                    setActiveStep(activeStep - 1);
                                    // handleReset();
                                  }}
                                  children={t("app.previous")}
                                  style={{
                                    marginRight: "10px",
                                    border: "1px solid #979797",
                                    color: "#757575",
                                    backgroundColor: "#fff",
                                    width: "100px !important",
                                  }}
                                  sx={{
                                    "&.MuiButton-root": {
                                      textTransform: "none",
                                      height: "32px",
                                      width: "100px",
                                    },
                                    "&.Mui-disabled": {
                                      color: "white",
                                    },
                                  }}
                                />
                            )}
                              {activeStep !== steps.length - 1 && (
                                  <LoadingButton
                                    type="submit"
                                    disabled={!isValid} //!(isValid && dirty)
                                    loading={loading}
                                    children={t("app.next")}
                                    style={{
                                      marginLeft: "10px",
                                      color: "#fff",
                                      backgroundColor: "#FF7900",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                )}
                        </div>
                    </Form>
                  );
              }}
          </Formik>
      </>
    )
  };

  const AdministrativeContact = () => {
    // AdministrativeContact
    /*
      firstname: string;
      lastname: string;
      mobilePhone: string;
      email: string;
      civility: string;
      region: string;
      city: string;
      postalBox: string;
      administrativeContactFunction: string;
    */
    return (
      <>
          <Formik
                initialValues={administrativeForm}
                validationSchema={administrativeContactValidationSchema}
                onSubmit={(values, formikHelpers) => {
                  if(steps[activeStep + 1].code == "PR_FA") {
                    console.log("LOADATTACHEDFILES");
                    loadAttachedFiles(formikHelpers.setFieldValue);
                  }
                  setLoading(true);
                  submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
                 }
                }
                isInitialValid={administrativeForm ? true : false}
                // validateOnChange={false}
                validateOnMount={false}
                // initialStatus={true}
                enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
              >
                {({
                  isValid,
                  dirty,
                  errors,
                  isSubmitting,
                  values,
                  setFieldValue,
                  handleReset,
                  handleChange,
                  resetForm
                }) => {
                  console.log("isValid ", isValid);
                  console.log("dirty ", dirty);
                  console.log("ERRORS ", errors);
                  return (
                    <Form>
                        <div className="d-flex flex-column">
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="lastname"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.lastname")}
                                value={
                                  values?.lastname
                                    ? values?.lastname
                                    : administrativeForm?.lastname
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="firstname"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.firstname")}
                                value={
                                  values?.firstname
                                    ? values?.firstname
                                    : administrativeForm?.firstname
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="mobilePhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.mobilePhone")}
                                value={
                                  values?.mobilePhone
                                    ? values?.mobilePhone
                                    : administrativeForm?.mobilePhone
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="fixPhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.fixPhone")}
                                value={
                                  values?.fixPhone
                                    ? values?.fixPhone
                                    : administrativeForm?.fixPhone
                                }
                                
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="email"
                                setFieldValue={setFieldValue}
                                type="email"
                                label={t("projet.administratif.email")}
                                value={
                                  values?.email
                                    ? values?.email
                                    : administrativeForm?.email
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormField2Select
                                isMulti={false}
                                options={[
                                  { label: "Monsieur", value: "Monsieur" },
                                  { label: "Madame", value: "Madame" },
                                ]}
                                name="civility"
                                label={t("projet.administratif.civility")}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="city"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.cityOfContact")}
                                value={
                                  values?.city
                                    ? values?.city
                                    : administrativeForm?.city
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="region"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.regionOfContact")}
                                value={
                                  values?.region
                                    ? values?.region
                                    : administrativeForm?.region
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="postalBox"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.postalBoxOfContact")}
                                value={
                                  values?.postalBox
                                    ? values?.postalBox
                                    : administrativeForm?.postalBox
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="function"
                                setFieldValue={setFieldValue}
                                label={t("projet.operational.function")}
                                value={
                                  values?.function
                                    ? values?.function
                                    : administrativeForm?.function
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          {activeStep !== 0 && activeStep !== steps.length - 1 && (
                                <Button
                                  type="button"
                                  onClick={() => {
                                    reloadPreviousObject(
                                      steps[activeStep - 1].code,
                                      setFieldValue
                                    );
                                    setActiveStep(activeStep - 1);
                                    // handleReset();
                                  }}
                                  children={t("app.previous")}
                                  style={{
                                    marginRight: "10px",
                                    border: "1px solid #979797",
                                    color: "#757575",
                                    backgroundColor: "#fff",
                                    width: "100px !important",
                                  }}
                                  sx={{
                                    "&.MuiButton-root": {
                                      textTransform: "none",
                                      height: "32px",
                                      width: "100px",
                                    },
                                    "&.Mui-disabled": {
                                      color: "white",
                                    },
                                  }}
                                />
                            )}
                              {activeStep !== steps.length - 1 && (
                                  <LoadingButton
                                    type="submit"
                                    disabled={!isValid} //!(isValid && dirty)
                                    loading={loading}
                                    children={t("app.next")}
                                    style={{
                                      marginLeft: "10px",
                                      color: "#fff",
                                      backgroundColor: "#FF7900",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                )}
                          </div>
                    </Form>
                  );
              }}
          </Formik>
      </>
    )
  };

  const SignatoryAgreement = () => {
    // SignatoryAgreement
    /*
        lastname: string;
        firstname: string;
        mobilePhone: string;
        email: string;
        qualityOfSignatory: string;
    */
    return (
      <>
          <Formik
                initialValues={signatoryForm}
                validationSchema={signatoryAgreementValidationSchema}
                onSubmit={(values, formikHelpers) => {
                  if(steps[activeStep + 1].code == "PR_FA") {
                    console.log("LOADATTACHEDFILES");
                    loadAttachedFiles(formikHelpers.setFieldValue);
                  }
                  setLoading(true);
                  submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
                 }
                }
                isInitialValid={signatoryForm ? true : false}
                // validateOnChange={false}
                validateOnMount={false}
                // initialStatus={true}
                enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
              >
                {({
                  isValid,
                  dirty,
                  errors,
                  isSubmitting,
                  values,
                  setFieldValue,
                  handleReset,
                  handleChange,
                  resetForm
                }) => {
                  console.log("isValid ", isValid);
                  console.log("dirty ", dirty);
                  console.log("ERRORS ", errors);
                  return (
                    <Form>
                        <div className="d-flex flex-column">
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="lastname"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.lastname")}
                                value={
                                  values?.lastname
                                    ? values?.lastname
                                    : signatoryForm?.lastname
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="firstname"
                                setFieldValue={setFieldValue}
                                label={t("projet.administratif.firstname")}
                                value={
                                  values?.firstname
                                    ? values?.firstname
                                    : signatoryForm?.firstname
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="mobilePhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.signataires.mobilePhone")}
                                value={
                                  values?.mobilePhone
                                    ? values?.mobilePhone
                                    : signatoryForm?.mobilePhone
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="mixPhone"
                                type="phone"
                                setFieldValue={setFieldValue}
                                label={t("projet.signataires.fixPhone")}
                                value={
                                  values?.fixPhone
                                    ? values?.fixPhone
                                    : signatoryForm?.fixPhone
                                }
                                
                              />
                            </div>
                          </div>
                          <div className="row mb-4">
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="email"
                                setFieldValue={setFieldValue}
                                type="email"
                                label={t("projet.signataires.email")}
                                value={
                                  values?.email
                                    ? values?.email
                                    : signatoryForm?.email
                                }
                                required
                              />
                            </div>
                            <div className="col-12 col-md-6">
                              <FormFieldInput
                                name="qualityOfSignatory"
                                setFieldValue={setFieldValue}
                                label={t("projet.signataires.qualityOfSignatory")}
                                value={
                                  values?.qualityOfSignatory
                                    ? values?.qualityOfSignatory
                                    : signatoryForm?.qualityOfSignatory
                                }
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end">
                            {activeStep !== 0 &&
                                activeStep !== steps.length - 1 && (
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      reloadPreviousObject(
                                        steps[activeStep - 1].code,
                                        setFieldValue
                                      );
                                      setActiveStep(activeStep - 1);
                                      // handleReset();
                                    }}
                                    children={t("app.previous")}
                                    style={{
                                      marginRight: "10px",
                                      border: "1px solid #979797",
                                      color: "#757575",
                                      backgroundColor: "#fff",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                            )}
                            {activeStep !== steps.length - 1 && (
                                <LoadingButton
                                  type="submit"
                                  disabled={!isValid} //!(isValid && dirty)
                                  loading={loading}
                                  children={t("app.next")}
                                  style={{
                                    marginLeft: "10px",
                                    color: "#fff",
                                    backgroundColor: "#FF7900",
                                    width: "100px !important",
                                  }}
                                  sx={{
                                    "&.MuiButton-root": {
                                      textTransform: "none",
                                      height: "32px",
                                      width: "100px",
                                    },
                                    "&.Mui-disabled": {
                                      color: "white",
                                    },
                                  }}
                                />
                              )}
                        </div>
                    </Form>
                  );
              }}
          </Formik>
      </>
    )
  };

  const Confirmation = () => {
    return (
        <Formik
          initialValues={{}}
          onSubmit={(values,formikHelpers) => {
             submitStep(steps[activeStep].code, values, formikHelpers.resetForm);
           }
          }
          // validateOnChange={false}
          validateOnMount={false}
          // initialStatus={true}
          enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
        >
          {({ errors, isSubmitting, values, setFieldValue}) => {
            
            return (
              <> 
                <Form>
                    <ProjectRecap projectId={project?.id} />
                    <div className="d-flex justify-content-end">
                          {activeStep !== 0 &&
                            activeStep !== steps.length - 1 && (
                              <Button
                                type="button"
                                onClick={() => {
                                  reloadPreviousObject(
                                    steps[activeStep - 1].code,
                                    setFieldValue
                                  );
                                  setActiveStep(activeStep - 1);
                                  // handleReset();
                                }}
                                children={t("app.previous")}
                                style={{
                                  marginRight: "10px",
                                  border: "1px solid #979797",
                                  color: "#757575",
                                  backgroundColor: "#fff",
                                  width: "100px !important",
                                }}
                                sx={{
                                  "&.MuiButton-root": {
                                    textTransform: "none",
                                    height: "32px",
                                    width: "100px",
                                  },
                                  "&.Mui-disabled": {
                                    color: "white",
                                  },
                                }}
                              />
                          )}
                          
                          {activeStep === steps.length - 1 && (
                            <LoadingButton
                              type="submit"
                              variant="contained"
                              className="px-1"
                              children={t("app.save")}
                              loading={loading}
                              style={{
                                marginLeft: "10px",
                                color: "#fff",
                                backgroundColor: "#FF7900",
                                width: "100px !important",
                              }}
                              sx={{
                                "&.MuiButton-root": {
                                  textTransform: "none",
                                  height: "32px",
                                  width: "100px",
                                },
                                "&.Mui-disabled": {
                                  color: "white",
                                },
                              }}
                            />
                          )}
                      </div>
                  </Form>
              </>
        );
      }}
    </Formik>
    );
  };

  const getFile = (name: string, event: FileUploadHandlerEvent, setFieldValue: any) => {
    // const target = event.target as HTMLInputElement;
    const files = event.files;
  
    if (files && files.length > 0) {
      if (name == "applicationFile") setApplicationFile(files[0] as any);
      if (name == "statuts") setStatuts(files[0] as any);
      if (name == "otherFile") setOtherFile(files[0] as any);
      if (name == "budget") setBudget(files[0] as any);
      if (name == "chronogram") setChronogram(files[0] as any);
      if (name == "previousAnnualReport") setPreviousAnnualReport(files[0] as any);

      if (setFieldValue != null) {
        setFieldValue(name, files[0] as any);
      }
    }
  };


const chooseOptions = {label: t('app.choose'), className: 'btn btn-primary' };
 
  const Attachment = () => {
    // Attachment
    /*
        applicationFile: File;
        statuts: File;
        source: File;
        previousAnnualReport: string;
        budget: string;
        chronogram: string;
    */
    // loadAttachedFiles(formikProps.setFieldValue);
    return (
      <>
          <Formik
                initialValues={attachmentForm}
                validationSchema={attachmentValidationSchema}
                onSubmit={(values, formikHelpers) => {
                  setLoading(true);
                  submitStep(steps[activeStep].code, values);
                 }
                }
                isInitialValid={applicationFile ? true : false}
                // validateOnChange={false}
                validateOnMount={false}
                // initialStatus={true}
                enableReinitialize={true} // Réinitialise les valeurs et désactive la validation lors du premier rendu
              >
                {({
                  isValid,
                  dirty,
                  errors,
                  isSubmitting,
                  values,
                  setFieldValue,
                  handleReset,
                  handleChange,
                  resetForm
                }) => {
                  console.log("isValid ", isValid);
                  console.log("dirty ", dirty);
                  console.log("ERRORS ", errors);
                  return (
                    <Form>
                          <div className="d-flex flex-column">
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  name="applicationFile"
                                  required
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  type="file"
                                  label={t("projet.attachment.applicationFile")}
                                  accept="/"
                              */}
                                <FormFieldFileChooser name="applicationFile" 
                                        label={t("projet.attachment.applicationFile")}
                                        customUpload  
                                        onChange={(e: FileUploadHandlerEvent) => getFile("applicationFile", e, setFieldValue) } 
                                        accept="*/*" 
                                        refe={fileApplicationFileRef}
                                        chooseOptions={chooseOptions}
                                        errors={errors}
                                        required
                                />
                                
                              </div>
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  name="statuts"
                                  required
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  type="file"
                                  label={t("projet.attachment.statuts")}
                                  accept="/"
                                /> */}
                                <FormFieldFileChooser name="statuts" 
                                    label={t("projet.attachment.statuts")}
                                    customUpload  
                                    onChange={(e: FileUploadHandlerEvent) => getFile("statuts", e, setFieldValue)}
                                    accept="*/*" 
                                    refe={fileStatutsRef}
                                    chooseOptions={chooseOptions}
                                    required
                                    errors={errors}
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  name="otherFile"
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  type="file"
                                  label={t("projet.attachment.otherFile")}
                                  accept="/"
                                /> */}
                                <FormFieldFileChooser name="otherFile" 
                                    label={t("projet.attachment.otherFile")}
                                    customUpload  
                                    onChange={(e: FileUploadHandlerEvent) => getFile("otherFile", e, setFieldValue)} 
                                    accept="*/*" 
                                    refe={fileOtherFileRef}
                                    chooseOptions={chooseOptions}
                                    errors={errors}
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  type="file"
                                  name="previousAnnualReport"
                                  required
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  label={t("projet.attachment.previousAnnualReport")}
                                  accept="/"
                                /> */}
                                <FormFieldFileChooser name="previousAnnualReport" 
                                      label={t("projet.attachment.previousAnnualReport")}
                                      customUpload  
                                      onChange={(e: FileUploadHandlerEvent) => getFile("previousAnnualReport", e, setFieldValue)} 
                                      required
                                      accept="*/*" 
                                      refe={filePreviousAnnualReportRef}
                                      chooseOptions={chooseOptions} 
                                      errors={errors}
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  type="file"
                                  name="budget"
                                  required
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  label={t("projet.attachment.budget")}
                                  accept="/"
                                /> */}
                                <FormFieldFileChooser name="budget" 
                                        label={t("projet.attachment.budget")}
                                        customUpload  
                                        onChange={(e: FileUploadHandlerEvent) => getFile("budget", e, setFieldValue)} 
                                        required
                                        accept="*/*" 
                                        refe={fileBudgetRef}
                                        chooseOptions={chooseOptions}
                                        errors={errors}
                                />
                              </div>
                              <div className="col-12 col-md-6">
                                {/* <FormFieldInput
                                  type="file"
                                  name="chronogram"
                                  required
                                  onChange={(e: ChangeEvent) => getFile(e, formikProps)}
                                  label={t("projet.attachment.chronogram")}
                                  accept="/"
                                /> */}
                                <FormFieldFileChooser name="chronogram" 
                                      label={t("projet.attachment.chronogram")}
                                      customUpload  
                                      onChange={(e: FileUploadHandlerEvent) => getFile("chronogram", e, setFieldValue)} 
                                      required
                                      accept="*/*" 
                                      refe={fileChronogramRef}
                                      chooseOptions={chooseOptions}
                                      errors={errors}
                                />
                              </div>
                            </div>
                            <div className="row mb-4">
                              <div className="col-12 col-md-6">
                                <span style={{ fontSize: "13px", color: "#CD3C14" }}>NB:</span> <span className="span field-error">{ t('app.filemaxsize') }</span>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-end">
                              {activeStep !== 0 &&
                                  activeStep !== steps.length - 1 && (
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        reloadPreviousObject(
                                          steps[activeStep - 1].code,
                                          setFieldValue
                                        );
                                        setActiveStep(activeStep - 1);
                                        // handleReset();
                                      }}
                                      children={t("app.previous")}
                                      style={{
                                        marginRight: "10px",
                                        border: "1px solid #979797",
                                        color: "#757575",
                                        backgroundColor: "#fff",
                                        width: "100px !important",
                                      }}
                                      sx={{
                                        "&.MuiButton-root": {
                                          textTransform: "none",
                                          height: "32px",
                                          width: "100px",
                                        },
                                        "&.Mui-disabled": {
                                          color: "white",
                                        },
                                      }}
                                    />
                              )}
                              {activeStep !== steps.length - 1 && (
                                  <LoadingButton
                                    type="submit"
                                    disabled={!isValid} //!(isValid && dirty)
                                    loading={loading}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      /*resetValidationSchema(
                                        steps[activeStep + 1].code
                                      );*/
                                      if(steps[activeStep + 1].code == "PR_FA") {
                                        console.log("LOADATTACHEDFILES");
                                        loadAttachedFiles(setFieldValue);
                                      }
                                      // handleReset();
                                      // resetForm();
                                      submitStep(steps[activeStep].code, values, handleReset);
                                      
                                    }}
                                    children={t("app.next")}
                                    style={{
                                      marginLeft: "10px",
                                      color: "#fff",
                                      backgroundColor: "#FF7900",
                                      width: "100px !important",
                                    }}
                                    sx={{
                                      "&.MuiButton-root": {
                                        textTransform: "none",
                                        height: "32px",
                                        width: "100px",
                                      },
                                      "&.Mui-disabled": {
                                        color: "white",
                                      },
                                    }}
                                  />
                                )}
                          </div>
                    </Form>
                  );
              }}
          </Formik>
      </>
    )
  };

  const loadAttachedFiles = async(setFieldValue: any) => {
    if(project?.idAttachment) {
        let pFA = await getAttachment(project?.idAttachment!);
        if(pFA) {
          const fileURLs = [];

          if(pFA.statuts) fileURLs.push({ name: "statuts", url: 'https://'+ pFA.statuts });
          if(pFA.previousAnnualReport) fileURLs.push({ name: "previousAnnualReport", url: 'https://'+ pFA.previousAnnualReport });
          if(pFA.otherFile) fileURLs.push({ name: "otherFile", url: 'https://'+ pFA.otherFile });
          if(pFA.chronogram) fileURLs.push({ name: "chronogram", url: 'https://'+ pFA.chronogram });
          if(pFA.budget) fileURLs.push({ name: "budget", url: 'https://'+ pFA.budget });
          if(pFA.applicationFile) fileURLs.push({name: "applicationFile", url: 'https://'+ pFA.applicationFile});

          for(let i=0; i < fileURLs.length; i++) {
            let urlObj = fileURLs[i];
            let filenameSplit = urlObj.url.split("/");
            let filename = filenameSplit[filenameSplit.length - 1];
            try {
              const response = await axios.get(urlObj.url, { responseType: 'arraybuffer' });

              let file = new File([response.data], filename, { type: response.headers["content-type"] } );
              if (urlObj.name == "applicationFile") {
                 setApplicationFile(file as any);
                  fileApplicationFileRef.current?.setFiles([file] as any);
              }
              if (urlObj.name == "statuts") {
                setStatuts(file as any); 
                fileStatutsRef.current?.setFiles([file] as any);
              }
              if (urlObj.name == "otherFile") {
                setOtherFile(file as any);
                fileOtherFileRef.current?.setFiles([file] as any);
              }
              if (urlObj.name == "budget") {
                setBudget(file as any);
                fileBudgetRef.current?.setFiles([file] as any);
              }
              if (urlObj.name == "chronogram") {
                setChronogram(file as any);
                fileChronogramRef.current?.setFiles([file] as any);
              }
              if (urlObj.name == "previousAnnualReport") {
                setPreviousAnnualReport(file as any);
                filePreviousAnnualReportRef.current?.setFiles([file] as any);
              }
              if(setFieldValue) setFieldValue(urlObj.name, file);
            } catch(e) {
              console.log("Erreur ", e);
            }
          }
        }
    }
  };

  /*const steps = [
    'GeneralInfos',
    'SignatoryAgreement',
    'AdministrativeContact',
    'OperationalContact',
    'StructureBankAccount',
    'Attachment',
    'Confirmation'
  ]; */
  const codes = ["PR_GI", "PR_SA", "PR_AC", "PR_OC", "PR_SBA", "PR_FA"];
  let steps = [] as StepProp[];
  for (let i = 0; i < codes.length; i++) {
    for (let j = 0; j < axe.sections.length; j++) {
      if (axe.sections[j].code == codes[i]) {
        steps.push({
          label: axe.sections[j].label,
          code: axe.sections[j].code,
        } as StepProp);
      }
    }
  }

  // Ajout du formulaire recapitulatif des saisis sur le projet
  steps.push({
    label: t('app.recap'),
    code: "Confirmation",
  } as StepProp);

  function getForm(code: string) {
    switch (code) {
      case "PR_GI":
        return GeneralInfos(); // Code: PR_GI
      case "PR_SA":
        return SignatoryAgreement(); // Code: PR_SA
      case "PR_AC":
        return AdministrativeContact(); // Code: PR_AC
      case "PR_OC":
        return OperationalContact(); // Code: PR_OC
      case "PR_SBA":
        return StructureBankAccount(); // Code: PR_SBA
      case "PR_FA":
        return Attachment(); // Code: PR_FA
      case "Confirmation":
        return Confirmation(); // Code: Confirmation
      default:
        return null;
    }
  }

  function getSectionComponent() {
    for (let i = 0; i < steps.length; i++) {
      if (activeStep == i) {
        return getForm(steps[i].code);
      }
    }

    /*switch(activeStep) {
      case 0: return GeneralInfos(formikProps); // Code: PR_GI
      case 1: return SignatoryAgreement(formikProps); // Code: PR_SA
      case 2: return AdministrativeContact(formikProps); // Code: PR_AC
      case 3: return OperationalContact(formikProps);  // Code: PR_OC
      case 4: return StructureBankAccount(formikProps);  // Code: PR_SBA
      case 5: return Attachment(formikProps);  // Code: PR_FA

      case 6: return Confirmation(formikProps);
      default: return null;
    } */
  }

  const reloadPreviousObject = async (code: string, setFieldValue?: any) => {
    switch (code) {
      case "PR_GI": {
        // Project
        if(project?.id) {
          let pProject = await getProject(project?.id!);
          let newProjectForm = {
            name: pProject.name,
            summary: pProject.summary,
            beneficiaryNumber: pProject.beneficiaryNumber,
            locationCountry: pProject.locationCountry,
            locationCity: pProject.locationCity,
            financialSupportRequest: pProject.financialSupportRequest,
            startDate: pProject.startDate,
            endDate: pProject.endDate,
            step: pProject.step,
            provisionalBudget: pProject.provisionalBudget,
            useOfSupportRequest: pProject.useOfSupportRequest,
            otherFinancialPartner: pProject.otherFinancialPartner,
            comment: pProject.comment,
            emailAccount: pProject.emailAccount,
            consultantInvolved: pProject.consultantInvolved,
            status: pProject.status,
          } as ProjectDto;
          setProject(newProjectForm);
        }
        break;
      }
      case "PR_SA": {
        // SignatoryAgreement
        if(project?.idSignatoryAgreement) {
          let pSA = await getSignatoryAgreement(project?.idSignatoryAgreement!);
          let newSignatoryForm = {
            lastname: pSA.lastname,
            firstname: pSA.firstname,
            mobilePhone: pSA.mobilePhone,
            email: pSA.email,
            qualityOfSignatory: pSA.qualityOfSignatory,
            fixPhone: pSA.fixPhone,
          } as SignatoryAgreementDto;
          setSignatoryForm(newSignatoryForm);
        }
        break;
      }
      case "PR_AC": {
        // AdministrativeContact
        if(project?.idAdministrativeContact) {
          let pAC = await getAdministrativeContact(project?.idAdministrativeContact!);
          let newAdminForm = {
            firstname: pAC.firstname,
            lastname: pAC.lastname,
            mobilePhone: pAC.mobilePhone,
            email: pAC.email,
            civility: pAC.civility,
            fixPhone: pAC.fixPhone,
            region: pAC.region,
            city: pAC.city,
            postalBox: pAC.postalBox,
            function: pAC.function
          } as AdministrativeContactDto;
          setAdministrativeForm(newAdminForm);
        }
        break;
      }
      case "PR_OC": {
        // OperationalContact
        if(project?.idOperationalContact) {
          let pOC = await getOperationalContact(project?.idOperationalContact!);
          let newOperationalForm = {
            firstname: pOC.firstname,
            lastname: pOC.lastname,
            function: pOC.function,
            regionOfContact: pOC.regionOfContact,
            cityOfContact: pOC.cityOfContact,
            postalBoxOfContact: pOC.postalBoxOfContact,
            streetAndNumber: pOC.streetAndNumber,
            mobilePhone: pOC.mobilePhone,
            email: pOC.email,
            departmentOfContact: pOC.departmentOfContact,
            adresse: pOC.adresse,
            fixPhone: pOC.fixPhone,
            districtOfContact: pOC.districtOfContact,
          } as OperationalContactDto;
          setOperationalForm(newOperationalForm);
        }
        break;
      }
      case "PR_SBA": {
        // StructureBankAccount
        if(project?.idStructureBankAccount) {
          let pSBA = await getStructureBankAccount(project?.idStructureBankAccount!);
          let newBankForm = {
            accountOwner: pSBA.accountOwner,
            bankName: pSBA.bankName,
            bankAddress: pSBA.bankAddress,
            cityOfBank: pSBA.cityOfBank,
            accountNumber: pSBA.accountNumber,
            poBox: pSBA.poBox,
            iban: pSBA.iban,
            swiftCode: pSBA.swiftCode,
          } as StructureBankAccountDto;
          setBankForm(newBankForm);
        }
        break;
      }
      case "PR_FA": {
        // Attachment
        loadAttachedFiles(setFieldValue);
        break;
      }
      default: {
        break;
      }
    }
  }


  return (
    <main>
      <section>
        <div className="row h-370 pt-50">
          <div className="col-md-6">
            <div className="project-text-header">
              <h1>
                {t("app.axeChoice")}:{" "}
                <span className="color-primary">{axe?.label}</span>
              </h1>
              <p>
                {t("app.recommendation")} <br />
                <br />
                NB:{" "}
                <strong className="color-primary">* {t("app.required")}</strong>
              </p>
            </div>
          </div>
          <div className="col-md-6">
            <img className="img-position" src={boule} alt="header image" />
          </div>
        </div>
        <div id="card-content">
          <div className="row">
            <div className="col-md-7">
              <div className="card mmb-7 ml-3 form-card">
                <div className="card-body pl-3 pr-3">
                    <div>
                      { getSectionComponent() } 
                    </div>
                </div>
              </div>
            </div>
            <div className="col-md-5">
              <div className="card stepper-card">
                <div className="card-body">
                  <h4>
                    {axe?.label} {t("app.cameroun")}{" "}
                  </h4>
                  <br />
                  <VerticalStepper steps={steps} activeStep={activeStep} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <div className="bg-orange change-request-title d-flex ">
          Enregistrement d'une attestation
          <div className="ml-auto d-flex">      
            <Button variant="text" type='button' onClick={() => navigate(-1)} startIcon={<TiArrowBack className="return-button"/> } title="Retourner" />
          </div>
        </div> */}
    </main>
  );
};

export default ProjectsForm;
